import React, { useState } from "react";
import { compose, withHandlers } from "recompose";
import { Control, actions } from "react-redux-form";
import { Row, Col, Button } from "reactstrap";
import {
  Page,
  FormGroup,
  required,
  DatePicker,
  withLocalForm,
  SubmitButton,
  onMount,
  withReload,
} from "components/common";
import { withContactSearch, LineItems, withXeroErrors } from "components";
import invariant from "invariant";
import Select from "react-select";
import deepPick from "deepPick";
import { NotificationManager } from "react-notifications";
import { formatDate } from "services/util";
import moment from "moment";
import BackButton from "components/refactoring/BackButton";
import { filterAndValidateLineItems, getBlankLineItems } from "components/LineItems";

export const EditTransaction = ({
  LocalForm,
  title,
  typeLabel,
  onSubmit,
  state,
  dispatch,
  xero,
  types,
  ContactSearch,
  XeroErrors,
}) => {
  const [addReference, setAddReference] = useState(false)
  const isSpendReceive = state?.Type === "RECEIVE" || state?.Type === "SPEND"
  return (
    <Page title={title}>
      <BackButton />
      <XeroErrors />
      <LocalForm
        onSubmit={onSubmit}
        validators={{
          Type: { required },
          Contact: { required },
          LineItems: (v) => v.length > 0,
          BankAccount: { required },
          Date: { required },
        }}
      >
        <FormGroup label={typeLabel} addControlClass={false} className="pt-2 pb-2">
          <Control.select style={{ backgroundColor: '#F4F7FE' }}
            component={Select}
            model=".Type"
            options={types}
            disabled={!!state.BankTransactionID}
            clearable={false}
            searchable={false}
            simpleValue
            changeAction={(m, v) => {

              dispatch(actions.change(m, v));
              dispatch(actions.change("local.LineItems", getBlankLineItems()));
              if (v === "SPEND-OVERPAYMENT" || v === "RECEIVE-OVERPAYMENT") {
                const first = state.LineItems[0];
                dispatch(actions.change("local.LineAmountTypes", "NoTax"));
                dispatch(
                  actions.change("local.LineItems", [
                    {
                      ...first,
                      AccountCode: v === "SPEND-OVERPAYMENT" ? "800" : "610",
                      TaxType: null,
                      Quantity: 1,
                    },
                  ])
                );
              }
            }}
          />
        </FormGroup>
        <Row>
          <Col>
            <FormGroup model=".Contact" label="Contact" addControlClass={false} className="pt-2 pb-2">
              <ContactSearch model=".Contact" style={{ backgroundColor: '#F4F7FE' }} />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup model=".Date" label="Date" className="pt-2 pb-2">
              <DatePicker model=".Date" className="form-control" />
            </FormGroup>
          </Col>

          {isSpendReceive &&
            <Col>
              <FormGroup label={(state.Reference || addReference) ? "Reference" : "Automated Reference Number"} model=".Reference" className="pt-2 pb-2">
                <div className="d-flex">
                  <Control.text disabled={!addReference} model=".Reference" placeholder={addReference ? "" : ((state.Type === "RECEIVE" ? "RCM" : "SPM") + "-AUTO")} />
                  <Button outline size="sm" color={addReference ? "danger" : "success"} onClick={() => setAddReference(!addReference)}><i className={`fa fa-${addReference ? "lock" : "unlock"}`} /></Button>
                </div>
              </FormGroup>
            </Col>
          }
        </Row>
        <hr />
        <LineItems
          item={state}
          includeItems={state.Type === "SPEND" || state.Type === "RECEIVE"}
          dispatch={dispatch}
          xero={xero}
          hideAddLines={
            state.Type === "SPEND-OVERPAYMENT" ||
            state.Type === "RECEIVE-OVERPAYMENT"
          }
          accountReadOnly={
            state.Type === 'SPEND-OVERPAYMENT' ||
            state.Type === 'RECEIVE-OVERPAYMENT'
          }
          quantityReadOnly={
            state.Type === "SPEND-OVERPAYMENT" ||
            state.Type === "RECEIVE-OVERPAYMENT"
          }
        />
        <div className="d-flex justify-content-end">
          <SubmitButton color="success" onClick={() => onSubmit()}>
            Save
          </SubmitButton>
          <div className="mx-1" />
          <SubmitButton
            color="primary"
            onClick={() => onSubmit({ addAnother: true })}
          >
            Save & add another
          </SubmitButton>
        </div>
      </LocalForm>
    </Page>
  )
};

export const enhance = compose(
  withReload(),
  withXeroErrors(),
  onMount(({ xero, match }) => () => {
    const { type, account_id, id } = match.params;

    invariant(
      type === "spend" || type === "receive",
      "type must be spend or receive"
    );

    const types = {
      spend: [
        { label: "Direct Payment", value: "SPEND" },
        { label: "Prepayment", value: "SPEND-PREPAYMENT" },
        { label: "Overpayment", value: "SPEND-OVERPAYMENT" },
      ],
      receive: [
        { label: "Direct Payment", value: "RECEIVE" },
        { label: "Prepayment", value: "RECEIVE-PREPAYMENT" },
        { label: "Overpayment", value: "RECEIVE-OVERPAYMENT" },
      ],
    }[type];

    let title = id === "new" ? "New " : "Edit ";
    title =
      type === "spend" ? `${title} Spend Money` : `${title} Receive Money`;

    return xero
      .get(`accounts/${account_id}`)
      .get("data")
      .then((account) => {
        return Promise.props({
          state:
            id === "new"
              ? {
                Date: formatDate(moment()),
                Type: types[0].value,
                LineAmountTypes: "Exclusive",
                BankAccount: {
                  AccountID: account.AccountID,
                },
                LineItems: getBlankLineItems(),
              }
              : xero.get(`banktransactions/${id}`).get("data"),
          types,
          title,
          typeLabel: type === "spend" ? "Spent as" : "Received as",
        });
      });
  }),
  withLocalForm({
    initialState: (props) => props.state,
  }),
  withHandlers({
    onSubmit:
      ({
        state,
        xero,
        history,
        match,
        location,
        t,
        reload,
        dispatch,
        setXeroErrors,
      }) => async ({ addAnother } = {}) => {
        const id = state.BankTransactionID;
        dispatch(actions.setPending("local", true));
        const isSpendReceive = state.Type === "RECEIVE" || state.Type === "SPEND"

        const item = deepPick(state, [
          "BankTransactionID",
          "Type",
          "Contact.ContactID",
          "LineItems",
          "BankAccount.AccountID",
          "Date",
          "Reference",
          "CurrencyCode",
          "CurrencyRate",
          "LineAmountTypes",
        ]);

        if (!item.Reference && isSpendReceive) {
          const data = await xero.get(`banktransactions`, {
            params: {
              page: 1,
              order: "Date DESC",
              where: `(BankAccount.AccountID=Guid("${item.BankAccount.AccountID}") AND Type="${item.Type}")`,
            }
          }).get("data")

          let maxNumber = 0;
          const prefix = item.Type === "RECEIVE" ? "RCM" : "SPM";

          for (const d of data) {

            if (d?.Reference?.startsWith(prefix)) {
              const parts = d.Reference.split("-");
              const num = parseInt(parts[1]);
              if (num > maxNumber) {
                maxNumber = num;
              }
            }
          }

          const newReference = `${prefix}-${(maxNumber + 1).toString().padStart(4, '0')}`;
          item.Reference = newReference;
        }

        const { result: LineItems, error } = filterAndValidateLineItems(
          dispatch
        )(item.LineItems);

        if (error) {
          return;
        }

        return (
          id
            ? xero.put(`banktransactions/${id}`, {
              ...item,
              LineItems,
            })
            : xero.post("banktransactions", {
              ...item,
              LineItems
            })
        )
          .get("data")
          .then(() => {
            NotificationManager.success(t("Transaction successfully saved"));
            if (addAnother) {
              if (id === "new") {
                reload();
              } else {
                const nextLocation = history.resolvePath("../new/");
                history.push(nextLocation);
                reload();
              }
            } else {
              const nextLocation = history.resolvePath("../..");
              history.push(nextLocation);
            }
          }, setXeroErrors)
          .finally(() => dispatch(actions.setPending("local", false)));
      },
  }),
  withContactSearch()
);

export default enhance(EditTransaction);
